import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';

interface SessionWarningProps {
  remainingTime: number; // in milliseconds
  onContinue: () => void;
  onExpire: () => void;
}

/**
 * Component to display a warning when the user's session is about to expire
 */
const SessionWarning: React.FC<SessionWarningProps> = ({ 
  remainingTime, 
  onContinue, 
  onExpire 
}) => {
  const [timeLeft, setTimeLeft] = useState<number>(remainingTime);
  const [open, setOpen] = useState<boolean>(true);
  
  // Count down the remaining time
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        const newTime = prevTime <= 1000 ? 0 : prevTime - 1000;
        
        if (newTime <= 0) {
          clearInterval(timer);
          onExpire();
          return 0;
        }
        return newTime;
      });
    }, 1000);
    
    return () => {
      clearInterval(timer);
    };
  }, [onExpire]);
  
  const handleContinue = () => {
    setOpen(false);
    onContinue();
  };
  
  // Format the time remaining in seconds
  const formatTime = (ms: number): string => {
    const seconds = Math.ceil(ms / 1000);
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  };
  
  return (
    <Dialog open={open} onClose={handleContinue}>
      <DialogTitle>Session Expiring</DialogTitle>
      <DialogContent>
        <Typography>
          Your session will expire in {formatTime(timeLeft)} due to inactivity.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Click "Continue Session" to stay logged in.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleContinue} color="primary" variant="contained">
          Continue Session
        </Button>
      </DialogActions>
    </Dialog>
  );

};

export default SessionWarning;
